import { getFileUploadUrl, uploadFileToS3 } from '@/shared/lib/file-upload';

export type AssetMask = {
  assetId: string;
  mimeType: string;
  metadata?: { fileName?: string; size?: number };
};

export type InputsWithMask = Record<string, unknown> & { mask?: string | AssetMask };

const hasOwn = (obj: object, key: string) => Object.prototype.hasOwnProperty.call(obj, key);

// Remove mask from input options
export function dropMaskFromOptions<T extends Record<string, unknown> | undefined>(options: T): T {
  if (!options) return options;
  if (!Object.prototype.hasOwnProperty.call(options, 'mask')) return options;

  const cleaned = Object.fromEntries(Object.entries(options).filter(([k]) => k !== 'mask'));

  return cleaned as unknown as T;
}

function base64ToBlob(base64: string, mimeType: string, sliceSize: number = 512) {
  const byteChars = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteChars.length; offset += sliceSize) {
    const slice = byteChars.slice(offset, offset + sliceSize);
    const byteNumbers = Uint8Array.from(slice, (c) => c.charCodeAt(0));
    byteArrays.push(byteNumbers);
  }

  return new Blob(byteArrays, { type: mimeType });
}

// Upload base64 PNG to storage and return the asset descriptor
async function uploadBase64Png(
  base64WithoutHeader: string,
  fileName = 'mask.png',
): Promise<AssetMask> {
  const blob = base64ToBlob(base64WithoutHeader, 'image/png');
  const { url, fields, assetId } = await getFileUploadUrl('image/png', fileName);

  const formData = new FormData();
  Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
  formData.append('file', new File([blob], fileName, { type: 'image/png' }));

  await uploadFileToS3(url, formData);

  return {
    assetId,
    mimeType: 'image/png',
    metadata: { size: blob.size, fileName },
  };
}

// Process mask input based on its type
export async function processInputsWithMask<T extends Record<string, unknown> | undefined>(
  inputs: T,
): Promise<T> {
  if (!inputs) return inputs;
  // eslint-disable-next-line i18next/no-literal-string -- input payload property name
  if (!hasOwn(inputs, 'mask')) return inputs;

  const mask = (inputs as InputsWithMask).mask;

  // Already processed to asset object
  if (mask && typeof mask === 'object' && 'assetId' in mask) {
    return inputs;
  }

  // Base64 string -> upload -> replace
  if (typeof mask === 'string' && mask.length > 0) {
    const uploaded = await uploadBase64Png(mask);
    return { ...inputs, mask: uploaded } as unknown as T;
  }

  // Empty/falsey mask -> drop key
  const cleaned = Object.fromEntries(Object.entries(inputs).filter(([k]) => k !== 'mask'));

  return cleaned as unknown as T;
}
